'use client'

import { useEffect, useRef, useState } from 'react'

import { GameShell } from '@/components/losi-games/game-shell'

import { mountGameCanvas } from '@/lib/losi-games/canvas'
import { difficultyScale, type Difficulty } from '@/lib/losi-games/difficulty'
import { drawChevronShip, drawInkDot, drawLobeEnemy } from '@/lib/losi-games/glyphs'
import { bestLocalScore, recordScoreAndWhisper } from '@/lib/losi-games/leaderboard'
import { resolveGameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

type Rock = { x: number; y: number; vx: number; vy: number; r: number; spin: number; ang: number }
type Bullet = { x: number; y: number; vx: number; vy: number; life: number }

export function AsteroidsGame({ colorId = 'creme' }: { colorId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(0)
  const [level, setLevel] = useState(1)
  const [menu, setMenu] = useState(true)
  const [over, setOver] = useState(false)
  const [won, setWon] = useState(false)
  const [whisper, setWhisper] = useState<LosiGameRankWhisper | null>(null)
  const [theme, setTheme] = useState(() => resolveGameTheme(colorId))
  const themeRef = useRef(theme)
  const startFn = useRef<(d: Difficulty) => void>()
  const restartFn = useRef<() => void>()

  useEffect(() => {
    const next = resolveGameTheme(colorId)
    themeRef.current = next
    setTheme(next)
  }, [colorId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    themeRef.current = resolveGameTheme(colorId)
    let cleanup: (() => void) | undefined

  setBest(bestLocalScore('asteroids'))
  const { ctx, size, dispose } = mountGameCanvas(canvas)

  let { w, h } = size()
  const ship = { x: w / 2, y: h / 2, vx: 0, vy: 0, ang: -Math.PI / 2, size: 18 }
  const rocks: Rock[] = []
  const bullets: Bullet[] = []
  let scoreAcc = 0
  let levelAcc = 1
  let playing = false
  let scale = 1
  let fireCd = 0
  let invuln = 0
  let last = performance.now()
  let alive = true
  let raf = 0

  const keys = { left: false, right: false, thrust: false, fire: false }

  const wrap = (v: number, max: number) => {
    if (v < -20) return max + 20
    if (v > max + 20) return -20
    return v
  }

  const spawnRocks = (n: number) => {
    rocks.length = 0
    ;({ w, h } = size())
    for (let i = 0; i < n; i++) {
      const ang = Math.random() * Math.PI * 2
      const dist = Math.min(w, h) * (0.28 + Math.random() * 0.22)
      const speed = (28 + Math.random() * 40) * scale
      rocks.push({
        x: w / 2 + Math.cos(ang) * dist,
        y: h / 2 + Math.sin(ang) * dist,
        vx: Math.cos(ang + 1.2) * speed,
        vy: Math.sin(ang + 1.2) * speed,
        r: 28 + Math.random() * 10,
        spin: (Math.random() - 0.5) * 2,
        ang: Math.random() * Math.PI * 2
      })
    }
  }

  const beginLevel = (lv: number) => {
    levelAcc = lv
    setLevel(lv)
    ship.x = w / 2
    ship.y = h / 2
    ship.vx = 0
    ship.vy = 0
    ship.ang = -Math.PI / 2
    bullets.length = 0
    invuln = 1.4
    spawnRocks(Math.min(14, 2 + lv + Math.floor((lv - 1) / 3)))
  }

  const splitRock = (r: Rock) => {
    const idx = rocks.indexOf(r)
    if (idx >= 0) rocks.splice(idx, 1)
    const pts = r.r > 22 ? 20 : r.r > 14 ? 40 : 80
    scoreAcc += pts
    setScore(scoreAcc)
    if (r.r > 14) {
      for (let i = 0; i < 2; i++) {
        const a = Math.random() * Math.PI * 2
        const sp = Math.hypot(r.vx, r.vy) * 1.15 + 20
        rocks.push({
          x: r.x,
          y: r.y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          r: r.r * 0.55,
          spin: (Math.random() - 0.5) * 3,
          ang: Math.random() * Math.PI * 2
        })
      }
    }
  }

  const finish = (didWin: boolean) => {
    playing = false
    setOver(!didWin)
    setWon(didWin)
    setWhisper(recordScoreAndWhisper('asteroids', scoreAcc))
    setBest(bestLocalScore('asteroids'))
  }

  startFn.current = (d: Difficulty) => {
    scale = difficultyScale(d)
    scoreAcc = 0
    setScore(0)
    setMenu(false)
    setOver(false)
    setWon(false)
    setWhisper(null)
    playing = true
    beginLevel(1)
  }

  restartFn.current = () => {
    setMenu(true)
    setOver(false)
    setWon(false)
    playing = false
    setBest(bestLocalScore('asteroids'))
  }

  const onKey = (e: KeyboardEvent) => {
    const down = e.type === 'keydown'
    if (e.key === 'ArrowLeft' || e.key === 'a') keys.left = down
    if (e.key === 'ArrowRight' || e.key === 'd') keys.right = down
    if (e.key === 'ArrowUp' || e.key === 'w') keys.thrust = down
    if (e.key === ' ' || e.key === 'Spacebar') {
      keys.fire = down
      e.preventDefault()
    }
  }
  window.addEventListener('keydown', onKey)
  window.addEventListener('keyup', onKey)

  const loop = (now: number) => {
    if (!alive) return
    const dt = Math.min(0.033, (now - last) / 1000)
    last = now
    ;({ w, h } = size())
    const th = themeRef.current

    if (playing) {
      invuln = Math.max(0, invuln - dt)
      fireCd = Math.max(0, fireCd - dt)

      if (keys.left) ship.ang -= 3.4 * dt
      if (keys.right) ship.ang += 3.4 * dt
      if (keys.thrust) {
        ship.vx += Math.cos(ship.ang) * 220 * dt
        ship.vy += Math.sin(ship.ang) * 220 * dt
      }
      ship.vx *= 1 - 0.55 * dt
      ship.vy *= 1 - 0.55 * dt
      const spd = Math.hypot(ship.vx, ship.vy)
      const maxSpd = 280 * scale
      if (spd > maxSpd) {
        ship.vx = (ship.vx / spd) * maxSpd
        ship.vy = (ship.vy / spd) * maxSpd
      }
      ship.x = wrap(ship.x + ship.vx * dt, w)
      ship.y = wrap(ship.y + ship.vy * dt, h)

      if (keys.fire && fireCd <= 0) {
        fireCd = 0.16
        bullets.push({
          x: ship.x + Math.cos(ship.ang) * ship.size * 0.6,
          y: ship.y + Math.sin(ship.ang) * ship.size * 0.6,
          vx: Math.cos(ship.ang) * 420 + ship.vx * 0.3,
          vy: Math.sin(ship.ang) * 420 + ship.vy * 0.3,
          life: 0.9
        })
      }

      for (const b of bullets) {
        b.x = wrap(b.x + b.vx * dt, w)
        b.y = wrap(b.y + b.vy * dt, h)
        b.life -= dt
      }
      for (let i = bullets.length - 1; i >= 0; i--) if (bullets[i]!.life <= 0) bullets.splice(i, 1)

      for (const r of rocks) {
        r.x = wrap(r.x + r.vx * dt, w)
        r.y = wrap(r.y + r.vy * dt, h)
        r.ang += r.spin * dt
      }

      for (let i = rocks.length - 1; i >= 0; i--) {
        const r = rocks[i]!
        for (let j = bullets.length - 1; j >= 0; j--) {
          const b = bullets[j]!
          if ((b.x - r.x) ** 2 + (b.y - r.y) ** 2 < (r.r + 3) ** 2) {
            bullets.splice(j, 1)
            splitRock(r)
            break
          }
        }
      }

      if (invuln <= 0) {
        for (const r of rocks) {
          if ((ship.x - r.x) ** 2 + (ship.y - r.y) ** 2 < (r.r * 0.85 + ship.size * 0.4) ** 2) {
            finish(false)
            break
          }
        }
      }

      if (playing && rocks.length === 0) {
        scoreAcc += levelAcc >= 6 ? 55 : 40
        setScore(scoreAcc)
        beginLevel(levelAcc + 1)
      }
    }

    ctx.fillStyle = th.paper
    ctx.fillRect(0, 0, w, h)
    for (const r of rocks) {
      drawLobeEnemy(ctx, r.x, r.y, r.r, 3 + (r.r > 20 ? 1 : 0), th.body)
    }
    for (const b of bullets) drawInkDot(ctx, b.x, b.y, 2.6, th.mark)
    if (playing) {
      ctx.save()
      if (invuln > 0) ctx.globalAlpha = 0.45 + 0.55 * Math.sin(now / 60)
      // Chevron tip is −y at ang 0; ship.ang is nose direction — offset by π/2
      drawChevronShip(ctx, ship.x, ship.y, ship.size, ship.ang + Math.PI / 2, th.body)
      ctx.restore()
    }

    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)
  cleanup = () => {
    alive = false
    cancelAnimationFrame(raf)
    dispose()
    window.removeEventListener('keydown', onKey)
    window.removeEventListener('keyup', onKey)
  }

    return () => {
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GameShell
      theme={theme}
      title="Asteroids"
      score={score}
      best={best}
      meta={`lvl ${level}`}
      menu={menu}
      over={over}
      won={won}
      whisper={whisper}
      hint="Arrow keys / WASD thrust & turn. Space to fire. Fields keep stacking."
      onStart={(d) => startFn.current?.(d)}
      onRestart={() => restartFn.current?.()}
    >
      <canvas ref={canvasRef} className="block touch-none" />
    </GameShell>
  )
}
